import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  readLanguageChoice,
  saveLanguageChoice,
  type SupportedLanguage,
} from "./language-preference";

// Lazy-load locale bundles — only the active language is downloaded.
// Add new languages by creating a file in ./locales/ and adding an entry to
// SUPPORTED_LANGUAGES in ./language-preference.ts.
const localeModules = import.meta.glob("./locales/*.json");

/**
 * BCP-47 tag every date and number formats through for a UI language —
 * `hi-IN` / `te-IN` bring the month and day names into that script (digits
 * stay Western, as Indian apps print them); anything else is `en-IN`.
 * Adding a language here is the one place its dates change too.
 */
const INTL_LOCALE: Record<string, string> = { hi: "hi-IN", te: "te-IN" };
export function intlLocaleFor(language: string | undefined): string {
  return INTL_LOCALE[(language ?? "").split("-")[0] ?? ""] ?? "en-IN";
}

// Lazy backend that resolves locale files via Vite's dynamic import
const lazyBackend = {
  type: "backend" as const,
  init: () => {},
  read: async (language: string, namespace: string, callback: (err: Error | null, data?: Record<string, unknown>) => void) => {
    const path = `./locales/${language}.json`;
    const loader = localeModules[path];
    if (!loader) {
      callback(new Error(`Locale "${language}" not found`));
      return;
    }
    try {
      const mod = (await loader()) as { default: Record<string, unknown> };
      // Each locale file is ONE json object keyed by namespace ("common",
      // "nav", "auth", …) — scope the bundle to just that namespace's
      // sub-object, the way a normal multi-namespace i18next setup expects
      // (`t("nav:items.dashboard")`, not a flat dot-path off the default
      // namespace). An unknown/not-yet-added namespace resolves to `{}`
      // rather than throwing, so a typo'd `ns:` prefix just falls back to
      // the caller's `defaultValue` instead of crashing the app.
      const scoped = (mod.default[namespace] as Record<string, unknown> | undefined) ?? {};
      callback(null, scoped);
    } catch (err) {
      callback(err as Error);
    }
  },
};

void i18n
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    // English unless this person explicitly chose another language. The
    // device language is deliberately NOT consulted — see language-preference.ts.
    lng: readLanguageChoice() ?? DEFAULT_LANGUAGE,
    fallbackLng: "en",
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
    defaultNS: "common",
    ns: [
      "common", "auth", "nav", "dashboard", "students", "attendance", "fees", "exams",
      "settings", "homework", "notices", "messages", "timetable", "transport", "dailyBrief",
    ],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    // Tests only: don't suspend a component while the lazy locale bundle
    // loads. Every `t()` call carries an English defaultValue identical to
    // en.json, so a test renders the same words either way — but with
    // suspense on, a render that races the dynamic import comes back as an
    // empty <div/> and the test fails intermittently. Production keeps
    // suspense so a Hindi reader never sees an English flash.
    react: { useSuspense: import.meta.env.MODE !== "test" },
  });

/** A person picked a language: remember it and switch to it. */
export function chooseLanguage(lang: SupportedLanguage): Promise<unknown> {
  saveLanguageChoice(lang);
  return i18n.changeLanguage(lang);
}

export { SUPPORTED_LANGUAGES, type SupportedLanguage };
export default i18n;
